// src/components/roads/AiRoadHelper.js
// --- UPDATE: v1.0 — FlowSense AI Road Helper
//
// Tells the user, for any road (being created, edited or viewed):
//   • WHAT data the road system needs, and HOW COMPLETE this road is
//     (AI-readiness score + checklist, weighted by how much each field matters)
//   • WHICH AI/ML features each field powers (remaining-life prediction,
//     flood & hurricane simulation, GIS map & heatmap, pothole clustering,
//     budgeting) and whether each feature is Ready / Partial / Blocked
//   • WHAT the information means — plain-language explanations for every field
//     and metric (FieldHint popovers), with units, examples and why it matters
//   • AI insights for existing roads (remaining life, overdue inspection,
//     flood exposure from elevation, status/condition mismatches, geometry
//     length mismatch, coordinates outside The Bahamas)
//
// Pure client-side and instant — no network calls. Works with the three road
// object shapes in the app: the API road (metadata.*), the Edit form (nested
// metadata) and the Create form (flat fields).
//
// Usage
//   <AiRoadHelper road={road} context="view" />                       full panel
//   <AiRoadHelper road={formData} context="create" variant="compact"
//                 onFix={(fieldId) => focusField(fieldId)} />         compact
//   <FieldHint field="elevation" />                                  (i) explainer
//   analyzeRoad(road, context)                                       data only
// --- END UPDATE
import React, { memo, useMemo, useState } from 'react';
import {
  Badge, Box, Button, CircularProgress, CircularProgressLabel, Collapse, Flex, HStack, Icon,
  IconButton, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Portal, Progress,
  SimpleGrid, Text, Tooltip, VStack, useColorModeValue,
} from '@chakra-ui/react';
import {
  MdOutlineAutoAwesome, MdCheckCircle, MdWarning, MdErrorOutline, MdInfoOutline, MdHelpOutline,
  MdPsychology, MdMap, MdOutlineWaterDrop, MdTimeline, MdAttachMoney, MdHub, MdArrowForward,
  MdExpandMore, MdExpandLess, MdOutlineTipsAndUpdates,
} from 'react-icons/md';

// ─────────────────────────────────────────────────────────────────────────────
// AI / ML features that consume road data
// ─────────────────────────────────────────────────────────────────────────────
export const AI_MODELS = Object.freeze({
  rsf: {
    label: 'Remaining-life prediction', short: 'RUL (RSF)', icon: MdTimeline, color: 'purple',
    explain: 'A Random Survival Forest model estimates how many years of service the road has left before it needs major rehabilitation. It learns from age, surface, traffic class and condition history.',
  },
  flood: {
    label: 'Flood & hurricane simulation', short: 'Flood / storm', icon: MdOutlineWaterDrop, color: 'blue',
    explain: 'The Digital Twin simulates storm surge, rainfall ponding and hurricane damage. It needs the road\'s position and surveyed elevation to know when it floods and how deep.',
  },
  gis: {
    label: 'GIS map & risk heatmap', short: 'GIS / heatmap', icon: MdMap, color: 'teal',
    explain: 'Drawing the road on the GIS map lets FlowSense colour it by predicted risk, show it to field crews and link nearby sensors and reports.',
  },
  cluster: {
    label: 'Pothole clustering', short: 'Clustering', icon: MdHub, color: 'orange',
    explain: 'Dashcam pothole detections are grouped (DBSCAN) and matched to the nearest mapped road to create work orders. Roads without geometry cannot receive them.',
  },
  budget: {
    label: 'Budgeting & reporting', short: 'Budget', icon: MdAttachMoney, color: 'green',
    explain: 'Length and width give the paved area used for cost estimates, km-by-constituency reports and the capital plan.',
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// Field guide — what each piece of information is and why the system needs it
// level: required | recommended | optional · weight: importance in the score
// contexts: where the field can be filled in (create / edit / view)
// ─────────────────────────────────────────────────────────────────────────────
const BAHAMAS = { minLat: 20.5, maxLat: 27.5, minLng: -80.5, maxLng: -72.0 };
const SEA_LEVEL_RISE_M = 0.23;
const SURGE_BY_CATEGORY = [['Category 1', 1.5], ['Category 2', 2.2], ['Category 3', 3.0], ['Category 4', 4.5], ['Category 5', 6.0]];

export const FIELD_GUIDE = Object.freeze({
  name: {
    label: 'Road name', level: 'required', weight: 3, usedBy: [], contexts: ['create', 'edit', 'view'], tab: 0,
    explain: 'The official street name residents and crews use.',
    why: 'Used everywhere a road is shown — search, work orders, citizen reports and dispatch.',
    example: 'Carmichael Road',
  },
  road_code: {
    label: 'Road code', level: 'recommended', weight: 2, usedBy: ['budget'], contexts: ['create', 'edit', 'view'], tab: 0,
    explain: 'A short unique ID for this road segment (Ministry or FlowSense numbering).',
    why: 'Names repeat (several "Market Street" segments); the code keeps work orders, inspections and reports attached to the right segment.',
    example: 'CR-001',
  },
  constituency: {
    label: 'Constituency', level: 'recommended', weight: 2, usedBy: ['budget'], contexts: ['create', 'edit', 'view'], tab: 0,
    explain: 'The electoral constituency the road is in.',
    why: 'Drives the per-constituency condition reports, budget allocation and MP briefings.',
    example: 'Golden Gates',
  },
  status: {
    label: 'Operational status', level: 'required', weight: 1, usedBy: [], contexts: ['create', 'edit', 'view'], tab: 0,
    explain: 'Whether the road is open normally, needs maintenance, is being worked on, closed or only planned.',
    why: 'Shown on the map and dashboards; roads marked "maintenance required" are prioritised for crews.',
    example: 'Operational',
  },
  location_description: {
    label: 'Location description', level: 'optional', weight: 1, usedBy: [], contexts: ['create', 'edit', 'view'], tab: 0,
    explain: 'Where the segment starts and ends, or nearby landmarks.',
    why: 'Helps crews find the exact stretch when coordinates are not enough.',
    example: 'From Faith Ave roundabout to Gladstone Rd',
  },
  classification: {
    label: 'Classification', level: 'recommended', weight: 3, usedBy: ['rsf', 'budget'], contexts: ['create', 'edit', 'view'], tab: 1,
    explain: 'The road\'s role in the network — highway and arterial roads carry heavier, faster traffic than local or residential streets.',
    why: 'Heavier traffic wears pavement faster, so the remaining-life model uses the class as a traffic-load proxy.',
    example: 'Arterial',
  },
  surface_type: {
    label: 'Surface material', level: 'recommended', weight: 3, usedBy: ['rsf', 'budget'], contexts: ['create', 'edit', 'view'], tab: 1,
    explain: 'What the road is surfaced with — asphalt, concrete, chip seal, gravel, etc.',
    why: 'Materials age at very different rates (concrete ≈ 30+ years, asphalt ≈ 15–20, chip seal ≈ 7–10). The remaining-life model and repair-cost estimates depend on it.',
    example: 'Asphalt',
  },
  length_m: {
    label: 'Length', unit: 'm', level: 'recommended', weight: 2, usedBy: ['budget'], contexts: ['create', 'edit', 'view'], tab: 1,
    explain: 'Segment length in metres. Drawing the road on the GIS map calculates this for you.',
    why: 'Used for km-of-network reports and, with width, the paved area that drives resurfacing cost.',
    example: '850',
  },
  width_m: {
    label: 'Width', unit: 'm', level: 'optional', weight: 1, usedBy: ['budget'], contexts: ['create', 'edit', 'view'], tab: 1,
    explain: 'Carriageway width in metres, kerb to kerb.',
    why: 'Paved area = length × width — the basis for paving and line-marking cost estimates.',
    example: '7.3',
  },
  lanes: {
    label: 'Number of lanes', level: 'optional', weight: 1, usedBy: ['rsf'], contexts: ['create', 'edit', 'view'], tab: 1,
    explain: 'Total traffic lanes in both directions.',
    why: 'Refines the traffic-load estimate and closure planning during works.',
    example: '2',
  },
  speed_limit_kmh: {
    label: 'Speed limit', unit: 'km/h', level: 'optional', weight: 1, usedBy: [], contexts: ['create', 'edit', 'view'], tab: 1,
    explain: 'Posted speed limit.',
    why: 'Used for traffic-management plans and pothole severity (a pothole is more dangerous at higher speed).',
    example: '50',
  },
  coordinates: {
    label: 'Location (lat / lng)', level: 'required', weight: 4, usedBy: ['flood', 'gis'], contexts: ['create', 'edit', 'view'], tab: 2,
    fieldIds: ['latitude', 'longitude'],
    explain: 'The centre point of the segment in decimal degrees. New Providence is around 25.0°N, −77.4°W (longitude is negative).',
    why: 'Places the road on the map and lets the weather and flood models fetch the forecast for the right spot.',
    example: '25.0443, −77.3963',
  },
  elevation: {
    label: 'Surveyed elevation', unit: 'm', level: 'recommended', weight: 3, usedBy: ['flood'], contexts: ['create', 'edit', 'view'], tab: 2,
    explain: 'Height of the road crown above mean sea level, from a survey or LiDAR.',
    why: 'Storm surge floods anything below the water line. Without it the simulations assume 2.5 m, which can badly under- or over-state flooding on low coastal roads.',
    example: '3.45',
  },
  install_date: {
    label: 'Install / last paved date', level: 'recommended', weight: 3, usedBy: ['rsf'], contexts: ['edit', 'view'],
    explain: 'When the road was built or last fully resurfaced.',
    why: 'Age is the single strongest driver of the remaining-life prediction.',
    example: '2016-05-01',
  },
  expected_lifespan_years: {
    label: 'Design lifespan', unit: 'years', level: 'optional', weight: 1, usedBy: ['rsf', 'budget'], contexts: ['edit'],
    explain: 'How long the surface was designed to last.',
    why: 'Used as the starting point for the remaining-life estimate and replacement planning.',
    example: '15',
  },
  asset_criticality: {
    label: 'Criticality', level: 'optional', weight: 1, usedBy: ['flood', 'budget'], contexts: ['edit', 'view'],
    explain: 'How important the road is — e.g. hospital, airport and evacuation routes are critical.',
    why: 'Critical roads are restored first after a storm and ranked higher for funding.',
    example: 'High',
  },
  geometry: {
    label: 'Drawn route (GIS)', level: 'recommended', weight: 3, usedBy: ['gis', 'cluster'], contexts: ['view'],
    explain: 'The road\'s actual path traced on the GIS map (Map tab → Unmapped roads → Draw).',
    why: 'Required for the risk heatmap and for matching dashcam pothole detections to this road. Also calculates the length automatically.',
    example: 'Traced on GIS map',
  },
  condition: {
    label: 'Condition assessment', level: 'recommended', weight: 3, usedBy: ['rsf'], contexts: ['view'],
    explain: 'A 0–100 pavement condition score from an inspection (higher is better).',
    why: 'Gives the remaining-life model a real observation instead of relying on age alone; scores below 55 auto-flag the road for maintenance.',
    example: '72 / 100 (Good)',
  },
  inspection: {
    label: 'Inspection schedule', level: 'optional', weight: 1, usedBy: ['rsf'], contexts: ['view'],
    explain: 'When the road was last inspected and when the next inspection is due.',
    why: 'Regular inspections keep condition data fresh; stale data makes predictions less reliable.',
    example: 'Every 12 months',
  },
});

/** Metric explanations for read-only values (View modal). */
export const METRIC_GUIDE = Object.freeze({
  rul: {
    label: 'AI predicted remaining useful life (RUL)',
    explain: 'How many years the road is expected to remain serviceable before it needs rehabilitation, predicted by the Random Survival Forest model.',
    scale: '≤ 2 years critical · ≤ 5 plan works · ≤ 8 monitor · > 8 healthy',
  },
  condition_score: {
    label: 'Pavement condition score',
    explain: 'A 0–100 rating from the latest inspection (similar to PCI). Below 55 the road is automatically flagged "maintenance required".',
    scale: '85+ excellent · 70–84 good · 55–69 fair · 40–54 poor · < 40 critical',
  },
  work_orders: {
    label: 'Active work orders',
    explain: 'Open maintenance jobs on this road. While any are open the road cannot be deleted.',
  },
  mapping: {
    label: 'Mapping status',
    explain: 'Whether the road\'s route has been drawn on the GIS map. Unmapped roads are excluded from the heatmap and pothole clustering.',
  },
  readiness: {
    label: 'AI readiness',
    explain: 'How much of the information FlowSense\'s AI models need is filled in for this road, weighted by importance. Higher readiness means more reliable predictions and simulations.',
  },
  // --- UPDATE: prediction metrics used by the road tables and AI panels
  risk_index: {
    label: 'AI risk index',
    explain: 'A 0–100 score combining the predicted remaining life with the latest condition score, overdue inspections, open work orders and storm-surge exposure. It ranks which roads need attention first — it is not a probability.',
    scale: '70+ critical · 50–69 high · 30–49 medium · < 30 low',
  },
  confidence: {
    label: 'Model confidence',
    explain: 'How sure the machine-learning model is about this prediction. It drops when inputs such as install date, surface or condition history are missing or stale.',
    scale: '80%+ high · 60–79% moderate · < 60% low — verify with an inspection',
  },
  rul_estimate: {
    label: 'Rule-based estimate',
    explain: 'No machine-learning prediction exists for this road yet, so FlowSense shows a rough estimate from design lifespan, age and condition score. Run the AI prediction once the key data is filled in for a real forecast.',
  },
  mapping_priority: {
    label: 'Mapping priority',
    explain: 'Which unmapped roads to draw first. Major roads (highway, arterial) and long segments rank highest because they carry the most traffic and pothole reports.',
    scale: 'High · Medium · Low',
  },
  ai_dispatch: {
    label: 'AI-dispatched work orders',
    explain: 'Dashcam footage is scanned by a computer-vision model for potholes and cracks. Nearby detections are grouped (DBSCAN clustering), matched to the closest mapped road, and a work order is created automatically with a priority based on severity and road class.',
  },
  // --- END UPDATE
});

// ─────────────────────────────────────────────────────────────────────────────
// Normalisation + analysis
// ─────────────────────────────────────────────────────────────────────────────
const present = (v) => v !== null && v !== undefined && String(v).trim() !== '';
const num = (v) => (present(v) && Number.isFinite(Number(v)) ? Number(v) : null);

const parsePolyline = (p) => {
  if (!p) return [];
  if (typeof p === 'string') { try { p = JSON.parse(p); } catch { return []; } }
  return Array.isArray(p) ? p.filter(pt => Number.isFinite(Number(pt?.lat)) && Number.isFinite(Number(pt?.lng))) : [];
};

const haversineLength = (pts) => {
  let m = 0;
  for (let i = 1; i < pts.length; i += 1) {
    const a = pts[i - 1], b = pts[i];
    const dLat = (b.lat - a.lat) * Math.PI / 180, dLng = (b.lng - a.lng) * Math.PI / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180);
    m += 2 * 6371000 * Math.asin(Math.sqrt(x));
  }
  return m;
};

/** Accepts API road (metadata.*), Edit form (nested metadata) or Create form (flat). */
export const normalizeRoad = (r = {}) => {
  const m = r.metadata || {};
  const pick = (k) => (present(r[k]) ? r[k] : m[k]);
  const poly = parsePolyline(r.polyline_points);
  return {
    name: r.name,
    road_code: pick('road_code'),
    constituency: pick('constituency'),
    status: r.status,
    location_description: r.location_description,
    classification: pick('classification') || m.road_class,
    surface_type: pick('surface_type'),
    length_m: num(pick('length_m')),
    width_m: num(pick('width_m')),
    lanes: num(pick('lanes')),
    speed_limit_kmh: num(pick('speed_limit_kmh')),
    latitude: num(r.latitude),
    longitude: num(r.longitude),
    elevation: num(r.elevation),
    install_date: r.install_date || m.last_paved_date || null,
    expected_lifespan_years: num(r.expected_lifespan_years),
    asset_criticality: r.asset_criticality,
    polyline: poly,
    hasGeometry: poly.length > 1 || !!r.geo_point,
    condition_score: num(m.condition_score),
    condition_rating: m.condition_rating,
    last_inspection_date: m.last_inspection_date || null,
    next_inspection_due: m.next_inspection_due || null,
    rul: num(r.ai_rul?.predicted_rul_years),
    open_wo: num(r.open_work_orders_count) || 0,
  };
};

const daysBetween = (a, b) => Math.round((b - a) / 86400000);

/**
 * @param {object} road     any of the three road shapes
 * @param {'create'|'edit'|'view'} context
 * @returns {{ score, grade, checks, models, insights, missingRequired, summary }}
 */
export function analyzeRoad(road, context = 'view') {
  const n = normalizeRoad(road || {});
  const checks = [];

  const add = (id, ok, extra = {}) => {
    const g = FIELD_GUIDE[id];
    if (!g || !g.contexts.includes(context)) return;
    checks.push({ id, ...g, status: ok === true ? 'ok' : ok === 'warn' ? 'warning' : 'missing', ...extra });
  };

  add('name', present(n.name));
  add('road_code', present(n.road_code));
  add('constituency', present(n.constituency));
  add('status', present(n.status));
  add('location_description', present(n.location_description));
  add('classification', present(n.classification));
  add('surface_type', present(n.surface_type));
  add('length_m', n.length_m > 0, n.length_m > 0 && n.hasGeometry && n.polyline.length > 1 && Math.abs(haversineLength(n.polyline) - n.length_m) / n.length_m > 0.2
    ? { status: 'warning', note: `Recorded ${Math.round(n.length_m)} m but the drawn route measures ${Math.round(haversineLength(n.polyline))} m.` }
    : {});
  add('width_m', n.width_m > 0, n.width_m != null && (n.width_m < 2.5 || n.width_m > 40) ? { status: 'warning', note: 'Width looks unusual (expected 2.5–40 m).' } : {});
  add('lanes', n.lanes > 0, n.lanes != null && (n.lanes < 1 || n.lanes > 8) ? { status: 'warning', note: 'Lane count looks unusual (expected 1–8).' } : {});
  add('speed_limit_kmh', n.speed_limit_kmh > 0, n.speed_limit_kmh != null && (n.speed_limit_kmh < 10 || n.speed_limit_kmh > 110) ? { status: 'warning', note: 'Speed limit looks unusual (expected 10–110 km/h).' } : {});

  // Coordinates: presence + plausibility (catches swapped / sign-flipped / foreign points)
  const hasCoords = n.latitude != null && n.longitude != null;
  let coordNote = null;
  if (hasCoords) {
    const inside = n.latitude >= BAHAMAS.minLat && n.latitude <= BAHAMAS.maxLat && n.longitude >= BAHAMAS.minLng && n.longitude <= BAHAMAS.maxLng;
    if (!inside) {
      const swapped = n.longitude >= BAHAMAS.minLat && n.longitude <= BAHAMAS.maxLat && n.latitude >= BAHAMAS.minLng && n.latitude <= BAHAMAS.maxLng;
      const signFlip = n.longitude > 0 && -n.longitude >= BAHAMAS.minLng && -n.longitude <= BAHAMAS.maxLng;
      coordNote = swapped ? 'Latitude and longitude look swapped.'
        : signFlip ? 'Longitude should be negative in The Bahamas (e.g. −77.39).'
        : 'This point is outside The Bahamas — it will distort maps, weather and simulations.';
    }
  }
  add('coordinates', hasCoords && !coordNote ? true : hasCoords ? 'warn' : false, coordNote ? { note: coordNote } : {});

  add('elevation', n.elevation != null, n.elevation != null && (n.elevation < -5 || n.elevation > 70)
    ? { status: 'warning', note: 'Elevation looks implausible for New Providence (−5 to 70 m).' }
    : n.elevation == null && hasCoords ? { note: 'Simulations will assume 2.5 m until this is surveyed.' } : {});
  add('install_date', present(n.install_date), present(n.install_date) && new Date(n.install_date) > new Date()
    ? { status: 'warning', note: 'Date is in the future.' } : {});
  add('expected_lifespan_years', n.expected_lifespan_years > 0);
  add('asset_criticality', present(n.asset_criticality));
  add('geometry', n.hasGeometry);
  add('condition', n.condition_score != null || present(n.condition_rating));
  add('inspection', present(n.next_inspection_due) || present(n.last_inspection_date));

  // Weighted score (warnings count half)
  let total = 0, got = 0;
  for (const c of checks) {
    total += c.weight;
    if (c.status === 'ok') got += c.weight;
    else if (c.status === 'warning') got += c.weight * 0.5;
  }
  const score = total ? Math.round((got / total) * 100) : 0;
  const grade = score >= 85 ? { label: 'AI-ready', color: 'green' } : score >= 60 ? { label: 'Partially ready', color: 'yellow' } : { label: 'Needs data', color: 'red' };

  // Per-model readiness
  const models = Object.entries(AI_MODELS).map(([key, model]) => {
    const relevant = checks.filter(c => c.usedBy.includes(key));
    if (!relevant.length) return null;
    const missingRequired = relevant.filter(c => c.status === 'missing' && (c.level === 'required' || c.weight >= 3));
    const okCount = relevant.filter(c => c.status === 'ok').length;
    const state = missingRequired.length ? 'blocked' : okCount === relevant.length ? 'ready' : 'partial';
    return { key, ...model, state, relevant, missing: relevant.filter(c => c.status !== 'ok') };
  }).filter(Boolean);

  // AI insights (meaningful mostly for existing roads)
  const insights = [];
  const now = Date.now();
  if (n.rul != null) {
    if (n.rul <= 2) insights.push({ severity: 'critical', title: `About ${n.rul} year${n.rul === 1 ? '' : 's'} of service left`, text: 'The AI expects this road to need rehabilitation very soon. Schedule an inspection and include resurfacing or reconstruction in the next budget cycle.' });
    else if (n.rul <= 5) insights.push({ severity: 'warning', title: `${n.rul} years of service predicted`, text: 'Plan preventive maintenance (crack sealing, patching, surface treatment) now — it is far cheaper than full reconstruction later.' });
    else insights.push({ severity: 'ok', title: `${n.rul} years of service predicted`, text: 'Healthy. Keep routine inspections on schedule so the prediction stays accurate.' });
  } else if (context === 'view') {
    insights.push({ severity: 'info', title: 'No remaining-life prediction yet', text: 'Run "Forecast Risk" on the Road Works page after adding surface, class and install date to get one.' });
  }
  if (n.condition_score != null && n.condition_score < 55 && n.status === 'operational') {
    insights.push({ severity: 'warning', title: 'Condition and status disagree', text: `Condition score is ${n.condition_score}/100 (below the 55 maintenance threshold) but the road is marked operational. Consider setting it to "Maintenance required".` });
  }
  if (n.rul != null && n.rul <= 2 && n.condition_score != null && n.condition_score >= 85) {
    insights.push({ severity: 'info', title: 'Prediction and inspection disagree', text: 'The AI predicts little remaining life but the last inspection scored the road excellent. Re-inspect to confirm — the inspection or the age data may be out of date.' });
  }
  if (present(n.next_inspection_due) && new Date(n.next_inspection_due).getTime() < now) {
    insights.push({ severity: 'warning', title: `Inspection overdue by ${daysBetween(new Date(n.next_inspection_due).getTime(), now)} days`, text: 'Overdue inspections let defects grow unnoticed and weaken the AI\'s condition data.' });
  } else if (present(n.last_inspection_date) && daysBetween(new Date(n.last_inspection_date).getTime(), now) > 365) {
    insights.push({ severity: 'info', title: 'Last inspection over a year ago', text: 'Condition data older than 12 months reduces prediction confidence. Schedule an inspection.' });
  }
  if (n.elevation != null) {
    const effective = n.elevation - SEA_LEVEL_RISE_M;
    const first = SURGE_BY_CATEGORY.find(([, surge]) => surge >= effective);
    if (effective < 1.8) insights.push({ severity: 'warning', title: 'Low-lying — high storm-surge risk', text: `At ${n.elevation} m this road is likely to flood in a ${first ? first[0] : 'major'} hurricane surge. Prioritise drainage and include it in pre-storm closure plans.` });
    else if (first) insights.push({ severity: 'info', title: `Surge exposure starts at ${first[0]}`, text: `At ${n.elevation} m the road stays above typical surge until a ${first[0]} storm (accounting for ${SEA_LEVEL_RISE_M} m sea-level rise).` });
  }
  if (context === 'view' && !n.hasGeometry) {
    insights.push({ severity: 'info', title: 'Not drawn on the GIS map', text: 'Trace it on the Map tab so it appears on the risk heatmap and can receive dashcam pothole reports.' });
  }
  if (n.open_wo > 0) {
    insights.push({ severity: 'info', title: `${n.open_wo} open work order${n.open_wo > 1 ? 's' : ''}`, text: 'Close completed work orders so the AI learns from the repairs and the condition data stays current.' });
  }
  if (n.surface_type && ['gravel', 'dirt', 'unpaved'].includes(String(n.surface_type).toLowerCase()) && ['highway', 'arterial', 'primary'].includes(String(n.classification || '').toLowerCase())) {
    insights.push({ severity: 'warning', title: 'Unpaved surface on a major road', text: 'Heavy traffic on an unpaved surface deteriorates quickly. Consider paving in the capital plan.' });
  }

  const missingRequired = checks.filter(c => c.status === 'missing' && c.level === 'required');
  const nextBest = checks.filter(c => c.status !== 'ok').sort((a, b) => b.weight - a.weight)[0] || null;
  const summary = missingRequired.length
    ? `Add ${missingRequired.map(c => c.label.toLowerCase()).join(' and ')} to save a usable record.`
    : nextBest
      ? `Next most valuable: ${nextBest.label.toLowerCase()} — ${nextBest.why.split('.')[0].toLowerCase()}.`
      : 'All the information FlowSense\'s AI uses is filled in.';

  return { score, grade, checks, models, insights, missingRequired, summary, normalized: n };
}

// ─────────────────────────────────────────────────────────────────────────────
// UI pieces
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_ICON = { ok: MdCheckCircle, warning: MdWarning, missing: MdErrorOutline };
const STATUS_COLOR = { ok: 'green.400', warning: 'orange.400', missing: 'red.400' };
const LEVEL_SCHEME = { required: 'red', recommended: 'purple', optional: 'gray' };
const MODEL_STATE = { ready: { label: 'Ready', scheme: 'green' }, partial: { label: 'Partial', scheme: 'yellow' }, blocked: { label: 'Blocked', scheme: 'red' } };
const INSIGHT_STYLE = {
  critical: { icon: MdErrorOutline, color: 'red.400' },
  warning: { icon: MdWarning, color: 'orange.400' },
  info: { icon: MdOutlineTipsAndUpdates, color: 'blue.400' },
  ok: { icon: MdCheckCircle, color: 'green.400' },
};

/** Small (i) button with a plain-language explanation of a field or metric. */
export const FieldHint = memo(({ field, metric, placement = 'top' }) => {
  const g = field ? FIELD_GUIDE[field] : METRIC_GUIDE[metric];
  const bg = useColorModeValue('white', 'gray.800');
  const muted = useColorModeValue('gray.600', 'gray.300');
  if (!g) return null;
  return (
    <Popover trigger="hover" placement={placement} isLazy openDelay={150}>
      <PopoverTrigger>
        <IconButton
          as="span" role="button" tabIndex={0}
          aria-label={`What is ${g.label}?`} icon={<Icon as={MdHelpOutline} />}
          size="xs" variant="ghost" minW="auto" h="auto" p={0.5} ml={1} color="purple.400"
          verticalAlign="middle" onClick={(e) => e.preventDefault()}
        />
      </PopoverTrigger>
      {/* Portal: hints live inside <label>/<p> elements — a block popover there is invalid DOM */}
      <Portal>
      <PopoverContent bg={bg} w="300px" borderRadius="xl" boxShadow="xl" textTransform="none" letterSpacing="normal" fontWeight="normal">
        <PopoverArrow bg={bg} />
        <PopoverBody p={3}>
          <HStack spacing={1.5} mb={1}>
            <Icon as={MdOutlineAutoAwesome} color="purple.400" />
            <Text fontWeight="800" fontSize="sm">{g.label}{g.unit ? ` (${g.unit})` : ''}</Text>
            {g.level && <Badge colorScheme={LEVEL_SCHEME[g.level]} fontSize="9px">{g.level}</Badge>}
          </HStack>
          <Text fontSize="xs" color={muted} mb={2}>{g.explain}</Text>
          {g.why && (
            <Text fontSize="xs" mb={2}><b>Why FlowSense needs it:</b> {g.why}</Text>
          )}
          {g.scale && <Text fontSize="xs" mb={2}><b>Scale:</b> {g.scale}</Text>}
          {g.example && <Text fontSize="xs" color={muted}>Example: <Text as="span" fontFamily="mono">{g.example}</Text></Text>}
          {g.usedBy?.length > 0 && (
            <HStack spacing={1} mt={2} flexWrap="wrap">
              {g.usedBy.map(k => <Badge key={k} colorScheme={AI_MODELS[k].color} fontSize="9px" variant="subtle">{AI_MODELS[k].short}</Badge>)}
            </HStack>
          )}
        </PopoverBody>
      </PopoverContent>
      </Portal>
    </Popover>
  );
});
FieldHint.displayName = 'FieldHint';

const ReadinessRing = memo(({ score, grade, size = '64px' }) => (
  <Tooltip label={METRIC_GUIDE.readiness.explain} hasArrow>
    <Box flexShrink={0}>
      <CircularProgress value={score} color={`${grade.color}.400`} size={size} thickness="10px" trackColor="blackAlpha.100">
        <CircularProgressLabel fontSize="sm" fontWeight="900">{score}%</CircularProgressLabel>
      </CircularProgress>
    </Box>
  </Tooltip>
));
ReadinessRing.displayName = 'ReadinessRing';

const CheckRow = memo(({ c, onFix, expanded, onToggle }) => {
  const hover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const canFix = onFix && c.status !== 'ok' && (c.contexts.includes('create') || c.contexts.includes('edit'));
  return (
    <Box borderRadius="md" _hover={{ bg: hover }} px={2} py={1.5}>
      <Flex align="center" gap={2}>
        <Icon as={STATUS_ICON[c.status]} color={STATUS_COLOR[c.status]} boxSize={4} flexShrink={0} />
        <Box flex={1} minW={0} as="button" type="button" textAlign="left" onClick={onToggle} aria-expanded={expanded}>
          <HStack spacing={1.5}>
            <Text fontSize="xs" fontWeight="700" noOfLines={1}>{c.label}</Text>
            {c.level !== 'optional' && c.status !== 'ok' && <Badge colorScheme={LEVEL_SCHEME[c.level]} fontSize="8px">{c.level}</Badge>}
          </HStack>
          {c.note && <Text fontSize="10px" color={STATUS_COLOR[c.status]} noOfLines={2}>{c.note}</Text>}
        </Box>
        <HStack spacing={0.5}>
          {c.usedBy.slice(0, 2).map(k => (
            <Tooltip key={k} label={AI_MODELS[k].label} hasArrow><Box><Icon as={AI_MODELS[k].icon} boxSize={3} color={`${AI_MODELS[k].color}.400`} /></Box></Tooltip>
          ))}
        </HStack>
        {canFix && (
          <Button size="xs" variant="ghost" colorScheme="purple" rightIcon={<Icon as={MdArrowForward} />} onClick={() => onFix(c.fieldIds?.[0] || c.id, c)}>
            {c.status === 'warning' ? 'Check' : 'Add'}
          </Button>
        )}
      </Flex>
      <Collapse in={expanded} animateOpacity>
        <Box pl={6} pt={1}>
          <Text fontSize="11px" color={muted}>{c.explain}</Text>
          <Text fontSize="11px" mt={1}><b>Why:</b> {c.why}</Text>
        </Box>
      </Collapse>
    </Box>
  );
});
CheckRow.displayName = 'CheckRow';

/**
 * @param {object}  road
 * @param {'create'|'edit'|'view'} context
 * @param {'full'|'compact'} variant
 * @param {(fieldId: string, check: object) => void} onFix
 */
const AiRoadHelper = ({ road, context = 'view', variant = 'full', onFix, defaultOpen }) => {
  const analysis = useMemo(() => analyzeRoad(road, context), [road, context]);
  const [open, setOpen] = useState(defaultOpen ?? variant === 'full');
  const [expandedId, setExpandedId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const shellBg = useColorModeValue('linear(135deg, purple.50, white 55%, blue.50)', 'linear(135deg, rgba(128,90,213,0.14), gray.800 55%, rgba(49,130,206,0.12))');
  const border = useColorModeValue('purple.100', 'whiteAlpha.200');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const { score, grade, checks, models, insights, summary } = analysis;
  const outstanding = checks.filter(c => c.status !== 'ok').sort((a, b) => b.weight - a.weight);
  const done = checks.filter(c => c.status === 'ok');
  const visibleChecks = showAll ? [...outstanding, ...done] : outstanding;

  return (
    <Box bgGradient={shellBg} borderWidth="1px" borderColor={border} borderRadius="xl" p={variant === 'compact' ? 3 : 4} role="region" aria-label="AI road helper">
      {/* Header */}
      <Flex align="center" gap={3}>
        <ReadinessRing score={score} grade={grade} size={variant === 'compact' ? '52px' : '64px'} />
        <Box flex={1} minW={0}>
          <HStack spacing={1.5}>
            <Icon as={MdPsychology} color="purple.500" />
            <Text fontWeight="900" fontSize="sm">AI Road Helper</Text>
            <Badge colorScheme={grade.color} fontSize="9px">{grade.label}</Badge>
          </HStack>
          <Text fontSize="xs" color={muted} mt={0.5} noOfLines={variant === 'compact' ? 2 : 3}>{summary}</Text>
        </Box>
        <IconButton size="sm" variant="ghost" aria-label={open ? 'Collapse AI helper' : 'Expand AI helper'}
          icon={<Icon as={open ? MdExpandLess : MdExpandMore} />} onClick={() => setOpen(o => !o)} />
      </Flex>

      {/* Model readiness strip */}
      <SimpleGrid columns={{ base: 2, md: variant === 'compact' ? 3 : 5 }} spacing={2} mt={3}>
        {models.map(m => (
          <Tooltip key={m.key} hasArrow label={`${m.explain}${m.missing.length ? ` Missing: ${m.missing.map(x => x.label.toLowerCase()).join(', ')}.` : ''}`}>
            <HStack bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" px={2} py={1.5} spacing={1.5}>
              <Icon as={m.icon} color={`${m.color}.400`} boxSize={3.5} />
              <Text fontSize="10px" fontWeight="700" flex={1} noOfLines={1}>{m.short}</Text>
              <Badge colorScheme={MODEL_STATE[m.state].scheme} fontSize="8px">{MODEL_STATE[m.state].label}</Badge>
            </HStack>
          </Tooltip>
        ))}
      </SimpleGrid>

      <Collapse in={open} animateOpacity>
        <VStack align="stretch" spacing={3} mt={3}>
          {/* Insights */}
          {insights.length > 0 && (
            <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={3}>
              <HStack spacing={1.5} mb={2}>
                <Icon as={MdOutlineAutoAwesome} color="purple.400" />
                <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">AI insights</Text>
              </HStack>
              <VStack align="stretch" spacing={2}>
                {insights.map(ins => (
                  <Flex key={ins.title} gap={2}>
                    <Icon as={INSIGHT_STYLE[ins.severity].icon} color={INSIGHT_STYLE[ins.severity].color} mt={0.5} flexShrink={0} />
                    <Box>
                      <Text fontSize="xs" fontWeight="800">{ins.title}</Text>
                      <Text fontSize="11px" color={muted}>{ins.text}</Text>
                    </Box>
                  </Flex>
                ))}
              </VStack>
            </Box>
          )}

          {/* Data checklist */}
          <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={2}>
            <Flex justify="space-between" align="center" px={2} pt={1} pb={1}>
              <HStack spacing={1.5}>
                <Icon as={MdInfoOutline} color="blue.400" />
                <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">
                  What the road system needs
                </Text>
              </HStack>
              <Text fontSize="10px" color={muted}>{done.length}/{checks.length} complete</Text>
            </Flex>
            <Progress value={checks.length ? (done.length / checks.length) * 100 : 0} size="xs" colorScheme={grade.color} borderRadius="full" mx={2} mb={1} />
            {visibleChecks.length === 0 ? (
              <Text fontSize="xs" color="green.400" px={2} py={2}>Everything FlowSense uses is filled in. 🎉</Text>
            ) : (
              <VStack align="stretch" spacing={0}>
                {visibleChecks.map(c => (
                  <CheckRow key={c.id} c={c} onFix={onFix}
                    expanded={expandedId === c.id}
                    onToggle={() => setExpandedId(id => (id === c.id ? null : c.id))} />
                ))}
              </VStack>
            )}
            {done.length > 0 && (
              <Button size="xs" variant="ghost" w="100%" mt={1} onClick={() => setShowAll(v => !v)}>
                {showAll ? 'Hide completed' : `Show ${done.length} completed`}
              </Button>
            )}
            <Text fontSize="10px" color={muted} px={2} pt={1}>Tip: click any item to see what it means and why it matters.</Text>
          </Box>
        </VStack>
      </Collapse>
    </Box>
  );
};

export default memo(AiRoadHelper);